import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isRequiredIf from 'react-proptype-conditional-require';

import P from '@deliveroo/consumer-component-library/components/P';
import Spacer from '@deliveroo/consumer-component-library/components/Spacer';

import OrderStatusIndicatorHeader from '../OrderStatusIndicatorHeader';
import OrderStatusProgressBar from '../OrderStatusProgressBar';
import OrderStatusCancelOrderButtonContainer from '../../containers/OrderStatusCancelOrderButton';

import styles from './styles.scss';

export default function OrderStatusProgress(props) {
  const {
    attributes: {
      advisory: lateOrderAdvisoryMessage,
      emphasizeAdvisory,
      progress,
      processingSteps,
      currentProgressPercentage,
      etaMessage,
      message,
      showLiveIndicator,
      formattedUpdatedAt,
      uiStatus,
      title: statusTitle,
      statusAnimationUrl,
      supplementaryMessage,
    },
    isNotFailedStatus,
    isPollingAndUnderPollLimit,
    isOverPollLimit,
    hasStatusButton,
    orderTrackerProgressAnimationFeatureOn,
    showFormattedSuccessMessage,
  } = props;

  const renderEstimationColumn = () => (
    <Fragment>
      <OrderStatusIndicatorHeader
        showLiveIndicator={showLiveIndicator}
        showReconnecting={isPollingAndUnderPollLimit}
        showLastUpdatedAt={isOverPollLimit}
        statusTitle={statusTitle}
        formattedUpdatedAt={formattedUpdatedAt}
        uiStatus={uiStatus}
        orderTrackerProgressAnimationFeatureOn={orderTrackerProgressAnimationFeatureOn}
      />
      {isNotFailedStatus && <h2 className={styles.eta}>{etaMessage}</h2>}
    </Fragment>
  );

  const renderAnimation = () => (
    <img
      src={statusAnimationUrl}
      alt={uiStatus}
      className={classnames(styles['progress-animation'], {
        [styles['hide-progress-animation']]: isOverPollLimit,
      })}
    />
  );

  const renderProgressBar = () => (
    <div className={styles['progress-bar']}>
      <OrderStatusProgressBar
        steps={processingSteps}
        progress={progress}
        currentProgressPercentage={currentProgressPercentage}
      />
    </div>
  );

  const renderLateAdvisory = () => (
    <P color={emphasizeAdvisory ? 'orange' : 'grey'} size="small" spacing="small 0 0 0">
      {lateOrderAdvisoryMessage}
    </P>
  );

  const renderSuccessMessage = () => (
    <div className={styles['success-message']}>
      {/* Header component does not support emojis */}
      <h3 className={styles.heading}>{statusTitle}</h3>
      <P color="grey" size="regular">
        {message}
      </P>
    </div>
  );

  const renderSupplementaryMessage = () => (
    <P color="grey" size="small" adjacentVerticalSpacing="x-small">
      {supplementaryMessage}
    </P>
  );

  return (
    <div className={styles['details-box']}>
      <div className={styles['details-header']}>
        <div className={styles['estimation-column']}>
          {uiStatus !== 'PENDING' && !showFormattedSuccessMessage && renderEstimationColumn()}
        </div>
        {orderTrackerProgressAnimationFeatureOn && statusAnimationUrl && renderAnimation()}
      </div>
      {progress && processingSteps && renderProgressBar()}
      {!showFormattedSuccessMessage && <P size="regular">{message}</P>}
      {showFormattedSuccessMessage && renderSuccessMessage()}
      {supplementaryMessage && renderSupplementaryMessage()}

      {hasStatusButton && (
        <Spacer spacing="small 0 0 0">
          <OrderStatusCancelOrderButtonContainer />
        </Spacer>
      )}
      {lateOrderAdvisoryMessage && renderLateAdvisory()}
    </div>
  );
}

OrderStatusProgress.defaultProps = {
  isPollingAndUnderPollLimit: false,
  isOverPollLimit: false,
  hasStatusButton: false,
  orderTrackerProgressAnimationFeatureOn: false,
  showFormattedSuccessMessage: false,
};

OrderStatusProgress.propTypes = {
  /** This is an object containing the attributes of the status */
  attributes: PropTypes.shape({
    /** This is a message for when the order is late */
    advisory: PropTypes.string,
    /** This is to emphasize that late message when the order is really late */
    emphasizeAdvisory: PropTypes.bool,
    currentProgressPercentage: PropTypes.number,
    etaMessage: isRequiredIf(PropTypes.string, (props) => props.isNotFailedStatus),
    formattedUpdatedAt: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    progress: PropTypes.shape({
      animationType: PropTypes.string,
      duration: PropTypes.number,
      finishAtPercentage: PropTypes.number,
      startAtPercentage: PropTypes.number,
    }),
    processingSteps: PropTypes.arrayOf(
      PropTypes.shape({
        endsAtProgressPercentage: PropTypes.number,
        isCurrent: PropTypes.bool,
        title: PropTypes.string,
      }),
    ),
    showLiveIndicator: PropTypes.bool.isRequired,
    statusAnimationUrl: PropTypes.string,
    title: PropTypes.string.isRequired,
    uiStatus: PropTypes.string.isRequired,
    supplementaryMessage: PropTypes.string,
  }).isRequired,
  /** Whether the order has a button related to the status such as the cancel order button */
  hasStatusButton: PropTypes.bool,
  /** The information banner displayed under order tracker */
  /** Has the order Failed? */
  isNotFailedStatus: PropTypes.bool.isRequired,
  /**
   * Is polling occuring, if so is it under the poll limit
   * If order is not live this is false
   */
  isPollingAndUnderPollLimit: PropTypes.bool,
  /**
   * Has the number of times the order details have been called
   * greater than the poll limit
   */
  isOverPollLimit: PropTypes.bool,
  /**
   * Show the order status progress animations
   * This is a feature variable
   */
  orderTrackerProgressAnimationFeatureOn: PropTypes.bool,
  /** show the success message in a formatted format */
  showFormattedSuccessMessage: PropTypes.bool,
};
